import { useEffect, useState } from 'preact/hooks';
import { ApiError, api } from '../../lib/api.js';
import { useApi } from '../../hooks/useApi.js';
import { Button } from '../../components/Button.jsx';
import { CHANNELS, CHANNEL_CODES } from '../../lib/channels.js';

const KIND_OPTIONS = [
  { value: 'proofer', label: 'Proofer' },
  { value: 'office',  label: 'Office'  },
];
const STATUS_OPTIONS = [
  { value: 'active',         label: 'Active' },
  { value: 'semi_active',    label: 'Semi-active' },
  { value: 'inactive',       label: 'Inactive' },
  { value: 'decommissioned', label: 'Decommissioned' },
];

const blank = {
  name: '', kind: 'proofer', categoryId: '', model: '', channels: [], physicalInksText: '', status: 'active',
  media: '', limitations: '', notes: '',
  ipAddress: '', macAddress: '', webManagerUrl: '',
  serialNumber: '', assetIdNumber: '', purchaseDate: '', location: '',
};

const Field = ({ label, hint, children, full = false }) => (
  <div class={full ? 'col-span-2' : ''}>
    <p class="mb-1 text-[12px] font-semibold text-text-tertiary">
      {label}
      {hint && <span class="ml-1 font-medium text-text-muted">· {hint}</span>}
    </p>
    {children}
  </div>
);

const Input = (props) => (
  <input
    {...props}
    class="block w-full rounded-md border border-border-strong bg-bg-card px-3 py-1.5 text-sm font-medium text-text-primary placeholder:text-text-muted focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
  />
);

const trimOrNull = (v) => {
  const s = (v ?? '').trim();
  return s === '' ? null : s;
};

/** Add/edit printer modal. Pass `printer` to edit, omit to create. */
export const PrinterEditModal = ({ open, onClose, onSaved, printer }) => {
  const editing = !!printer;
  const { data: categories, refetch: refetchCategories } = useApi(open ? '/equipment/categories' : null);
  const [form, setForm] = useState(blank);
  const [newCategory, setNewCategory] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setNewCategory('');
    setForm(editing ? {
      ...blank,
      ...printer,
      categoryId: printer.categoryId ?? '',
      // Normalise nulls → empty strings for the form inputs.
      model: printer.model ?? '',
      media: printer.media ?? '',
      limitations: printer.limitations ?? '',
      notes: printer.notes ?? '',
      ipAddress: printer.ipAddress ?? '',
      macAddress: printer.macAddress ?? '',
      webManagerUrl: printer.webManagerUrl ?? '',
      serialNumber: printer.serialNumber ?? '',
      assetIdNumber: printer.assetIdNumber ?? '',
      purchaseDate: printer.purchaseDate ?? '',
      location: printer.location ?? '',
      physicalInksText: (printer.physicalInks ?? []).join('\n'),
    } : blank);
  }, [open, editing, printer]);

  if (!open) return null;

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.currentTarget.value }));
  const toggleChannel = (code) => () =>
    setForm((f) => ({
      ...f,
      channels: f.channels.includes(code)
        ? f.channels.filter((c) => c !== code)
        : [...f.channels, code],
    }));

  const addCategory = async () => {
    const name = newCategory.trim();
    if (!name) return;
    try {
      const created = await api.post('/equipment/categories', { name });
      setNewCategory('');
      await refetchCategories();
      setForm((f) => ({ ...f, categoryId: created.id }));
    } catch (err) {
      setError(err);
    }
  };

  const save = async () => {
    if (!form.name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const physicalInks = form.physicalInksText
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean);
      const payload = {
        name:           form.name.trim(),
        kind:           form.kind,
        categoryId:     form.categoryId === '' ? null : Number(form.categoryId),
        model:          trimOrNull(form.model),
        channels:       form.kind === 'office' ? [] : form.channels,
        physicalInks:   form.kind === 'office' ? null : (physicalInks.length ? physicalInks : null),
        status:         form.status,
        media:          trimOrNull(form.media),
        limitations:    trimOrNull(form.limitations),
        notes:          trimOrNull(form.notes),
        ipAddress:      trimOrNull(form.ipAddress),
        macAddress:     trimOrNull(form.macAddress),
        webManagerUrl:  trimOrNull(form.webManagerUrl),
        serialNumber:   trimOrNull(form.serialNumber),
        assetIdNumber:  trimOrNull(form.assetIdNumber),
        purchaseDate:   trimOrNull(form.purchaseDate),
        location:       trimOrNull(form.location),
      };
      const saved = editing
        ? await api.patch(`/printers/${printer.id}`, payload)
        : await api.post('/printers', payload);
      onSaved?.(saved);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      class="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/55 px-4 py-10"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div class="w-full max-w-xl overflow-hidden rounded-xl border border-border-subtle bg-bg-base">
        <div class="border-b border-border-subtle bg-bg-header px-5 py-4">
          <p class="eyebrow mb-1" style={{ color: '#5DD0C8' }}>
            {editing ? 'Edit printer' : 'Add printer'}
          </p>
          <p class="m-0 font-serif text-lg font-medium text-text-primary">
            {editing ? printer.name : 'New printer'}
          </p>
        </div>

        <div class="space-y-5 p-5">

          {/* Identity */}
          <div class="grid grid-cols-2 gap-3">
            <Field label="Name"><Input value={form.name} onInput={set('name')} placeholder="e.g. Roland VS-300i" autoFocus /></Field>
            <Field label="Kind">
              <div class="flex overflow-hidden rounded-md border border-border-strong">
                {KIND_OPTIONS.map((o) => (
                  <button
                    key={o.value}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, kind: o.value }))}
                    class={`flex-1 px-3 py-1.5 text-[13px] font-semibold ${form.kind === o.value ? 'bg-ink-teal text-[#0E2622]' : 'bg-transparent text-text-tertiary hover:bg-bg-card'}`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </Field>

            <Field label="Model" hint="optional"><Input value={form.model} onInput={set('model')} placeholder="e.g. Roland VersaCAMM VS-300i" /></Field>
            <Field label="Status">
              <select
                value={form.status}
                onChange={set('status')}
                class="block w-full rounded-md border border-border-strong bg-bg-card px-3 py-1.5 text-sm font-medium text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
              >
                {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </Field>

            <Field label="Category" hint="or add a new one below" full>
              <select
                value={form.categoryId}
                onChange={set('categoryId')}
                class="block w-full rounded-md border border-border-strong bg-bg-card px-3 py-1.5 text-sm font-medium text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
              >
                <option value="">No category</option>
                {(categories ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Field>
          </div>

          <div class="flex items-end gap-2">
            <div class="flex-1">
              <Field label="New category" hint="e.g. wide-format, sample room">
                <Input
                  value={newCategory}
                  onInput={(e) => setNewCategory(e.currentTarget.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCategory(); } }}
                  placeholder="Category name"
                />
              </Field>
            </div>
            <Button variant="secondary" onClick={addCategory} disabled={!newCategory.trim()}>Add</Button>
          </div>

          {/* Channels + physical inks (proofer only) */}
          {form.kind === 'proofer' && (
            <>
              <div>
                <p class="mb-1.5 text-[12px] font-semibold text-text-tertiary">
                  Mixing channels <span class="font-medium text-text-muted">· logical signals the mixing software emits</span>
                </p>
                <div class="flex flex-wrap gap-2">
                  {CHANNEL_CODES.map((code) => {
                    const ch = CHANNELS[code];
                    const on = form.channels.includes(code);
                    return (
                      <button
                        key={code}
                        type="button"
                        onClick={toggleChannel(code)}
                        class={`flex items-center gap-2 rounded-full border px-3 py-1 text-[12px] font-semibold transition ${
                          on
                            ? 'border-current'
                            : 'border-border-strong text-text-tertiary hover:border-border-subtle'
                        }`}
                        style={on ? { color: ch.color, background: 'rgba(255,255,255,0.04)' } : undefined}
                      >
                        <span class="block h-2 w-2 rounded-full" style={{ background: ch.color }} />
                        {code.toUpperCase()} <span class="font-medium text-text-muted">{ch.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <p class="mb-1.5 text-[12px] font-semibold text-text-tertiary">
                  Physical inks <span class="font-medium text-text-muted">· one per line · syncs to Inventory when wired</span>
                </p>
                <textarea
                  rows={5}
                  value={form.physicalInksText}
                  onInput={set('physicalInksText')}
                  placeholder={'Cyan\nLight cyan\nVivid magenta\n…'}
                  class="block w-full resize-y rounded-md border border-border-strong bg-bg-card px-3 py-2 font-mono text-[13px] font-medium text-text-primary placeholder:text-text-muted focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                />
                <p class="mt-1 text-[11px] font-medium text-text-muted">
                  These are the actual cartridges loaded — different from mixing channels. The Epsons typically have more
                  physical inks than CMYKOG (Photo black, Light cyan, etc.).
                </p>
              </div>
            </>
          )}

          {/* Network */}
          <div>
            <p class="mb-2 text-[11px] font-semibold uppercase tracking-eyebrow text-text-tertiary">Network</p>
            <div class="grid grid-cols-2 gap-3">
              <Field label="IP address" hint="optional"><Input value={form.ipAddress} onInput={set('ipAddress')} placeholder="192.168.1.40" /></Field>
              <Field label="MAC address" hint="optional"><Input value={form.macAddress} onInput={set('macAddress')} placeholder="AA:BB:CC:DD:EE:FF" /></Field>
              <Field label="Web manager URL" hint="optional" full><Input value={form.webManagerUrl} onInput={set('webManagerUrl')} placeholder="http://192.168.1.40" /></Field>
            </div>
          </div>

          {/* Asset details */}
          <div>
            <p class="mb-2 text-[11px] font-semibold uppercase tracking-eyebrow text-text-tertiary">Asset</p>
            <div class="grid grid-cols-2 gap-3">
              <Field label="Serial number" hint="optional"><Input value={form.serialNumber} onInput={set('serialNumber')} /></Field>
              <Field label="Asset ID" hint="optional"><Input value={form.assetIdNumber} onInput={set('assetIdNumber')} placeholder="Your asset tag" /></Field>
              <Field label="Location" hint="optional"><Input value={form.location} onInput={set('location')} placeholder="Proofing room" /></Field>
              <Field label="Acquired" hint="YYYY-MM-DD"><Input value={form.purchaseDate} onInput={set('purchaseDate')} placeholder="2014-03-12" /></Field>
              <Field label="Media" hint="optional" full><Input value={form.media} onInput={set('media')} placeholder="e.g. clear film, white substrate" /></Field>
            </div>
          </div>

          {/* Limitations + notes */}
          <div>
            <p class="mb-2 text-[11px] font-semibold uppercase tracking-eyebrow text-text-tertiary">Operational</p>
            <Field label="Current limitations" hint="surfaces as a warning everywhere this printer appears">
              <Input value={form.limitations} onInput={set('limitations')} placeholder="e.g. Yellow ink offline" />
            </Field>
            <div class="mt-3">
              <Field label="Notes" hint="optional">
                <textarea
                  rows={2}
                  value={form.notes}
                  onInput={set('notes')}
                  class="block w-full resize-none rounded-md border border-border-strong bg-bg-card px-3 py-2 text-sm font-medium text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                />
              </Field>
            </div>
          </div>

          {error && (
            <p class="text-[13px] font-semibold text-ink-magenta">
              {error instanceof ApiError ? error.message : (error.message ?? 'Failed to save')}
            </p>
          )}
        </div>

        <div class="flex justify-end gap-2 border-t border-border-subtle bg-bg-header px-5 py-3">
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={save} disabled={busy || !form.name.trim()}>
            {busy ? 'Saving…' : editing ? 'Save changes' : 'Add printer'}
          </Button>
        </div>
      </div>
    </div>
  );
};
